import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import moviesApi from '../../client/movies-api'
import { MediaGridItem } from '../../types/Media'
import MediaGridPage from '../../areas/layout/media-grid-page'

type MovieCategory =
  | 'trending'
  | 'popular'
  | 'top-rated'
  | 'now-playing'
  | 'upcoming'
  | 'marvel'
  | 'netflix'
  | 'provider-hbo'
  | 'provider-apple'
  | 'provider-prime-video'
  | 'provider-hulu'
  | 'provider-disney'

const MoviesPage = () => {
  const { category } = useParams<{ category: MovieCategory }>()
  const currentCategory: MovieCategory = category || 'popular'

  const [items, setItems] = useState<MediaGridItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)

  const fetchMovies = async (page: number) => {
    switch (currentCategory) {
      case 'trending':
        return moviesApi.getTrending(page)
      case 'popular':
        return moviesApi.getPopular(page)
      case 'top-rated':
        return moviesApi.getTopRated(page)
      case 'now-playing':
        return moviesApi.getNowPlaying(page)
      case 'upcoming':
        return moviesApi.getUpcoming(page)
      case 'marvel':
        return moviesApi.getMarvel(page)
      case 'netflix':
        return moviesApi.getNetflix(page)
      case 'provider-hbo':
        return moviesApi.getByProvider('hbo', page)
      case 'provider-apple':
        return moviesApi.getByProvider('apple', page)
      case 'provider-prime-video':
        return moviesApi.getByProvider('prime-video', page)
      case 'provider-hulu':
        return moviesApi.getByProvider('hulu', page)
      case 'provider-disney':
        return moviesApi.getByProvider('disney', page)
      default:
        return moviesApi.getPopular(page)
    }
  }

  const loadPage = async (page: number) => {
    try {
      setLoading(true)

      const res = await fetchMovies(page)
      const data = res.data

      if (!data || !data.results || data.results.length === 0) {
        setHasMore(false)
        return
      }

      setItems((prev) => [
        ...prev,
        ...data.results.map((m: any) => ({
          id: m.id,
          type: 'movie' as const,
          title: m.title,
          year: m.year,
          rating: m.rating,
          posterUrl: m.posterUrl,
          genres: m.genres,
        })),
      ])

      setHasMore(page < data.totalPages)
    } catch {
      setError('Failed to load movies')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setItems([])
    setPage(1)
    setHasMore(true)
    loadPage(1)
  }, [currentCategory]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = () => {
    if (loading || !hasMore) return
    const next = page + 1
    setPage(next)
    loadPage(next)
  }

  const titleText = 'Movies'

  const subtitleText =
    currentCategory === 'popular'
      ? 'Popular Movies'
      : currentCategory === 'top-rated'
      ? 'Top Rated Movies'
      : currentCategory === 'now-playing'
      ? 'Now Playing'
      : currentCategory === 'upcoming'
      ? 'Upcoming Movies'
      : currentCategory === 'marvel'
      ? 'Marvel Movies'
      : currentCategory === 'netflix'
      ? 'Netflix Movies'
      : currentCategory === 'provider-hbo'
      ? 'HBO Movies'
      : currentCategory === 'provider-apple'
      ? 'Apple Movies'
      : currentCategory === 'provider-prime-video'
      ? 'Prime Video Movies'
      : currentCategory === 'provider-hulu'
      ? 'Hulu Movies'
      : currentCategory === 'provider-disney'
      ? 'Disney+ Movies'
      : ''

  return (
    <MediaGridPage
      title={titleText}
      subtitle={subtitleText}
      items={items}
      loading={loading}
      error={error}
      hasMore={hasMore}
      onLoadMore={handleLoadMore}
    />
  )
}

export default MoviesPage
